import toast from 'react-hot-toast';

import { addToCourseList, setCourseList } from '@/lib/slices/course-slice';
import { AppDispatch } from '@/lib/store';

import CourseCreationService from './course-creation-service';
import {
  CourseBuilderData,
  CourseData,
  ActivityBuilderData,
  SubunitBuilderData,
  UnitBuilderData,
} from 'course-types';
import UserCourseStorageService from '@/services/storage/user-course-storage-service';

const CourseEditService = {
  async handleCopyCourse(
    dispatch: AppDispatch,
    closeModal: () => void,
    courseName: string,
    activeCourseData: CourseData,
    copyActivities: boolean,
  ) {
    const courseBuilderData: CourseBuilderData = {
      name: `${courseName}`,
      units: Object.values(activeCourseData.units)
        .sort((a, b) => a.index - b.index)
        .map(
          (unit) =>
            ({
              name: unit.name || '',
              description: unit.description || '',
              index: unit.index,
              subunits: Object.values(unit.subunits || {})
                .sort((a, b) => a.index - b.index)
                .map(
                  (subunitData) =>
                    ({
                      name: subunitData.name || '',
                      index: subunitData.index,
                      activities: copyActivities
                        ? subunitData.activities.map(
                            (activity) =>
                              ({
                                name: activity.name || '',
                                url: activity.url || '',
                                index: activity.index ?? 0,
                                description: activity.description || '',
                                type: activity.type,
                                // Preserve id for quizzes and documents so the backend can
                                // look up the original entities when copying.
                                id:
                                  activity.type === 'quiz' ||
                                  activity.type === 'document' ||
                                  activity.type === 'assignment'
                                    ? (activity.id as string)
                                    : undefined,
                              }) as ActivityBuilderData & { id?: string },
                          )
                        : [],
                    }) as SubunitBuilderData,
                ),
              activities: copyActivities
                ? unit.activities.map(
                    (activity) =>
                      ({
                        name: activity.name || '',
                        url: activity.url || '',
                        index: activity.index ?? 0,
                        description: activity.description || '',
                        type: activity.type,
                        id:
                          activity.type === 'quiz' ||
                          activity.type === 'document' ||
                          activity.type === 'assignment'
                            ? (activity.id as string)
                            : undefined,
                      }) as ActivityBuilderData & { id?: string },
                  )
                : [],
            }) as UnitBuilderData,
        ),
    };

    const copyResponse = await CourseCreationService.copyCourse(courseBuilderData);
    if (!copyResponse || copyResponse.status !== 'success') {
      toast.error('Failed to copy course');
    } else {
      toast.success('Course copied successfully');
      const copiedCourseData: CourseData & { id: string } = copyResponse.data;
      dispatch(addToCourseList({ id: copiedCourseData.id as any, name: copiedCourseData.name }));
      // Refresh cookie from server so enterprise list remains canonical, then sync Redux
      await UserCourseStorageService.retrieveCourseCookie();
      const updatedCookie = UserCourseStorageService.getCourseCookie();
      if (updatedCookie) {
        dispatch(setCourseList(updatedCookie.all?.enterprise ?? []));
      }
      closeModal();
    }
  },
};

export default CourseEditService;
